import { useState, useCallback } from 'react';
import { credentialsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, Loader2, FileUp, Eye, EyeOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { CredentialIcon } from './CredentialIcon';
import { getCredentialTypeLabel } from '@/utils/credentialDefinitions';

interface CredentialPreviewItem {
  name: string;
  type: string;
  index?: number; // Added for unique identification
}

interface ImportResult {
  originalName: string;
  importedName: string;
  type: string;
  status: 'imported' | 'renamed' | 'skipped' | 'error';
  newId?: string;
  error?: string;
}

interface ImportCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

type Phase = 'upload' | 'password' | 'selection' | 'results';

export function ImportCredentialsDialog({
  open,
  onOpenChange,
  onImportComplete,
}: ImportCredentialsDialogProps) {
  const [phase, setPhase] = useState<Phase>('upload');
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [encryptedPayload, setEncryptedPayload] = useState<string>('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState<CredentialPreviewItem[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const resetState = useCallback(() => {
    setPhase('upload');
    setFileContent(null);
    setFileName(null);
    setEncryptedPayload('');
    setPassword('');
    setCredentials([]);
    setSelectedIndices(new Set());
    setError(null);
    setImportResults([]);
    setIsDragging(false);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.ewa') && !file.name.endsWith('.json')) {
      setError('Please select a .ewa or .json file');
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (!parsed.encryptedPayload || !parsed.version) {
        setError('Invalid export file format');
        return;
      }

      setFileContent(text);
      setFileName(file.name);
      setEncryptedPayload(parsed.encryptedPayload);
      setError(null);
      setPhase('password');
    } catch (e) {
      setError('Failed to parse file. Please select a valid export file.');
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDecrypt = async () => {
    if (!password) {
      setError('Please enter the password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await credentialsApi.importPreview(encryptedPayload, password);
      const previewData = response.data;

      if (!previewData.credentials || previewData.credentials.length === 0) {
        setError('No credentials found in file');
        return;
      }

      // Add index to each credential for unique identification
      const credentialsWithIndex = previewData.credentials.map((c: CredentialPreviewItem, idx: number) => ({
        ...c,
        index: idx
      }));
      setCredentials(credentialsWithIndex);
      // Select all by default
      setSelectedIndices(new Set(credentialsWithIndex.map((_: CredentialPreviewItem, idx: number) => idx)));
      setPhase('selection');
    } catch (error: any) {
      console.error('Failed to decrypt file:', error);
      const message = error?.response?.data?.error || 'Incorrect password or corrupted file';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (selectedIndices.size === 0) {
      setError('Please select at least one credential to import');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await credentialsApi.import(
        encryptedPayload,
        password,
        Array.from(selectedIndices)
      );
      const importData = response.data;

      setImportResults(importData.results || []);
      setPhase('results');

      const successCount = importData.imported;
      const renamedCount = importData.renamed;

      if (successCount > 0) {
        let message = `Imported ${successCount} credential${successCount !== 1 ? 's' : ''}`;
        if (renamedCount > 0) {
          message += ` (${renamedCount} renamed)`;
        }
        toast.success(message);
        onImportComplete();
      }
    } catch (error: any) {
      console.error('Failed to import credentials:', error);
      const message = error?.response?.data?.error || 'Failed to import credentials';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCredential = (index: number) => {
    setSelectedIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedIndices(new Set(credentials.map((_, idx) => idx)));
  };

  const deselectAll = () => {
    setSelectedIndices(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Credentials
          </DialogTitle>
          <DialogDescription>
            {phase === 'upload' && 'Select a credential export file (.ewa) to import'}
            {phase === 'password' && 'Enter the password used to encrypt the export'}
            {phase === 'selection' && 'Select which credentials to import'}
            {phase === 'results' && 'Import complete'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Phase 1: File Upload */}
          {phase === 'upload' && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <FileUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  Drag and drop your .ewa file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".ewa,.json"
                  className="hidden"
                  id="file-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                <Button asChild variant="outline">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    Browse Files
                  </label>
                </Button>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Phase 2: Password */}
          {phase === 'password' && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <span className="font-medium">File:</span> {fileName}
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-password">Password</Label>
                <div className="relative">
                  <Input
                    id="import-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter the export password"
                    className="pr-10"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleDecrypt();
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Phase 3: Selection */}
          {phase === 'selection' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {selectedIndices.size} of {credentials.length} selected
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>

              <ScrollArea className="h-[300px] border rounded-md">
                <div className="p-2 space-y-1">
                  {credentials.map((credential, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 hover:bg-muted rounded-lg cursor-pointer"
                      onClick={() => toggleCredential(index)}
                    >
                      <Checkbox
                        checked={selectedIndices.has(index)}
                        onCheckedChange={() => toggleCredential(index)}
                      />
                      <CredentialIcon type={credential.type} size="md" showBackground />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{credential.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {getCredentialTypeLabel(credential.type)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Phase 4: Results */}
          {phase === 'results' && (
            <div className="space-y-4">
              <ScrollArea className="h-[300px] border rounded-md">
                <div className="p-2 space-y-1">
                  {importResults.map((result, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                    >
                      {result.status === 'imported' || result.status === 'renamed' ? (
                        <div className="h-6 w-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                          <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </div>
                      )}
                      <CredentialIcon type={result.type} size="md" showBackground />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {result.status === 'renamed'
                            ? `${result.originalName} → ${result.importedName}`
                            : result.importedName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {result.status === 'imported' && 'Imported successfully'}
                          {result.status === 'renamed' && 'Imported with new name (duplicate)'}
                          {result.status === 'error' && (result.error || 'Failed to import')}
                          {result.status === 'skipped' && 'Skipped'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}

          {phase === 'password' && (
            <>
              <Button variant="outline" onClick={() => setPhase('upload')}>
                Back
              </Button>
              <Button onClick={handleDecrypt} disabled={!password || loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Decrypting...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </>
          )}

          {phase === 'selection' && (
            <>
              <Button variant="outline" onClick={() => setPhase('password')}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={selectedIndices.size === 0 || loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {selectedIndices.size} Credential{selectedIndices.size !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </>
          )}

          {phase === 'results' && (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
